import { getCountryByCode, getCityById, getEventsForCountry, getEventsForCity, getUniversityById, getEventsForUniversity, getEventsForReligionByCountry, getEventsForNatureByCountry } from '../data-source';
import { formatUniversityCityName } from '../university-utils';
import { isPublicHoliday } from '../event-filters';
import { getSlugEntry, getSlugForCountry, getUniversitySlugForId, getUniversitySlugEntry, getCitySlugForId, getCitySlugEntry, getCountryLensSlugForId, getCountryLensSlugEntry } from '../i18n';
import { getEventSlug } from '../event-slugs';
import { getMonthDisplay } from '../hub-pages';
import { formatDateLong } from '../date-format';
import { SITE_URL, SITE_NAME } from '../brand';
import type { CountryCode, CalendarEvent, EventType, ReligionSubLensId } from '../types';
import { YEAR, TYPE_DISPLAY, RELIGION_DISPLAY_NAMES } from './constants';

const MAX_DESCRIPTION_LENGTH = 155;
const MAX_TITLE_LENGTH = 60;

/** Truncate a meta description to stay within SERP display limits */
function truncateDescription(desc: string): string {
  if (desc.length <= MAX_DESCRIPTION_LENGTH) return desc;
  return desc.slice(0, MAX_DESCRIPTION_LENGTH - 1).replace(/\s+\S*$/, '') + '…';
}

/** Guard title length for SERP display (~60 chars). Drops brand suffix if needed, then truncates. */
function guardTitleLength(title: string, siteName: string): string {
  if (title.length <= MAX_TITLE_LENGTH) return title;
  // Try dropping " | SiteName" suffix
  const withoutBrand = title.replace(` | ${siteName}`, '');
  if (withoutBrand.length <= MAX_TITLE_LENGTH) return withoutBrand;
  // Truncate at word boundary
  return withoutBrand.slice(0, MAX_TITLE_LENGTH - 1).replace(/\s+\S*$/, '') + '…';
}

/** Builds SEO metadata (title, description, keywords, canonical) for a country page */
export function getCountrySeoData(code: CountryCode, lang: string = 'en') {
  const country = getCountryByCode(code);
  if (!country) return null;

  const slug = getSlugForCountry(code, lang);
  const entry = slug ? getSlugEntry(slug) : null;
  const events = getEventsForCountry(code);
  // "<N> public holidays" must count public holidays, not all country-lens
  // events (festivals/weather), to stay consistent with the FAQ + JSON-LD count
  // (almanak-564d).
  const holidayCount = events.filter(isPublicHoliday).length;
  const topEvents = events.slice(0, 5).map((e) => e.title);
  const remaining = Math.max(0, events.length - topEvents.length);

  // Use translated title from slug entry if available, otherwise fallback to English
  const title = entry?.pageTitle
    ?? `${country.name} Public Holidays ${YEAR} — ${country.flag} Calendar | ${SITE_NAME}`;

  const eventSnippet =
    topEvents.length > 0
      ? `${topEvents.join(', ')}${remaining > 0 ? ` and ${remaining} more public holidays` : ''}`
      : 'public holidays and observances';

  const description = entry?.pageDescription
    ?? `${country.flag} ${country.name} ${YEAR}: ${holidayCount} public holidays with business impact, bridge-day planner, and calendar export.`;

  const keywords = [
    `${country.name} public holidays ${YEAR}`,
    `${country.name} calendar ${YEAR}`,
    `${country.name} holidays`,
    ...topEvents.map((t) => `${t} ${YEAR}`),
  ];

  const canonical = slug ? `${SITE_URL}/${slug}` : `${SITE_URL}/countries/${code.toLowerCase()}`;

  return { title, description, keywords, canonical, country, events };
}

/** Builds SEO metadata for a city page */
export function getCitySeoData(cityId: string, lang: string = 'en') {
  const city = getCityById(cityId);
  if (!city) return null;

  const country = getCountryByCode(city.country);
  if (!country) return null;

  const events = getEventsForCity(cityId);

  // Use localized title/description from the slug entry
  const citySlug = getCitySlugForId(cityId, lang);
  const slugEntry = citySlug ? getCitySlugEntry(citySlug) : null;

  const title = slugEntry?.pageTitle ?? `${city.name} Festivals & Events ${YEAR} | ${SITE_NAME}`;
  const description = slugEntry?.pageDescription ?? `${city.name}, ${country.name} ${YEAR}: festivals, cultural events & local happenings. Free interactive calendar.`;

  const keywords = [
    `${city.name} festivals ${YEAR}`,
    `${city.name} events ${YEAR}`,
    `${city.name} calendar ${YEAR}`,
  ];

  const canonical = `${SITE_URL}/${citySlug || getCitySlugForId(cityId, 'en')}`;

  return { title, description, keywords, canonical, city, country, events };
}

/** Builds SEO metadata for an individual event detail page */
export function getEventSeoData(event: CalendarEvent, lang: string = 'en') {
  const country = getCountryByCode(event.country);
  const countryName = country?.name || event.country;
  const slug = getEventSlug(event.id);
  const canonical = `${SITE_URL}/events/${slug}`;
  const isGlobal = event.country === 'XR';

  // University name for academic events (prevents duplicate canonical across universities)
  const university = event.universityId ? getUniversityById(event.universityId) : undefined;
  const uniLabel = university?.abbrev || university?.name;

  // Format date with weekday: "Friday, April 3, 2026"
  const formattedDate = formatDateLong(event.dateStart, lang);

  // Title: include university name when available to differentiate across institutions
  // "Semester 1 Begins at UIAM — Monday, October 6, 2025 | vacant.day"
  // "Good Friday in Australia — Friday, April 3, 2026 | vacant.day"
  const locationLabel = uniLabel ? `at ${uniLabel}` : isGlobal ? '' : `in ${countryName}`;
  const rawTitle = locationLabel
    ? `${event.title} ${locationLabel} — ${formattedDate} | ${SITE_NAME}`
    : `${event.title} — ${formattedDate} | ${SITE_NAME}`;
  const title = guardTitleLength(rawTitle, SITE_NAME);

  // Description: branch on event type
  const isPublicHoliday = event.type === 'public_holiday' || event.isGazetted;
  const notesText = event.notes || '';
  let description: string;
  if (isPublicHoliday && !isGlobal) {
    description = `${event.title} is a public holiday in ${countryName} on ${formattedDate}.`;
    description += ` See the full ${countryName} ${YEAR} calendar.`;
    if (notesText) description += ` ${notesText}`;
  } else if (isGlobal) {
    description = `${event.title} falls on ${formattedDate}.`;
    description += ` View the complete ${YEAR} holiday calendar on ${SITE_NAME}.`;
    if (notesText) description += ` ${notesText}`;
  } else if (uniLabel) {
    description = `${event.title} at ${uniLabel} falls on ${formattedDate}.`;
    description += ` View the full ${uniLabel} academic calendar for ${YEAR}.`;
    if (notesText) description += ` ${notesText}`;
  } else {
    description = `${event.title} falls on ${formattedDate} in ${countryName}.`;
    description += ` View the complete ${countryName} holiday calendar for ${YEAR}.`;
    if (notesText) description += ` ${notesText}`;
  }

  const keywords = [
    `${event.title} ${YEAR}`,
    ...(isGlobal ? [] : [`${countryName} ${event.title}`]),
    `${event.type.replace(/_/g, ' ')} ${isGlobal ? YEAR : countryName}`,
    ...(isGlobal ? [] : [`${countryName} holidays ${YEAR}`]),
  ];

  return { title, description: truncateDescription(description), keywords, canonical };
}

/** Builds SEO metadata for a translated event detail page */
export function getEventSeoDataTranslated(
  event: CalendarEvent,
  lang: string,
  cachedTranslation?: { title: string; titleLocal: string | null; notes: string | null } | null,
) {
  const country = getCountryByCode(event.country);
  const countryName = country?.name || event.country;
  const slug = getEventSlug(event.id);
  const canonical = `${SITE_URL}/events/${slug}/${lang}`;

  // University name for academic events
  const university = event.universityId ? getUniversityById(event.universityId) : undefined;
  const uniLabel = university?.abbrev || university?.name;

  // Try to get country name in the target language from slug entry
  const countrySlug = getSlugForCountry(event.country, lang);
  const countryEntry = countrySlug ? getSlugEntry(countrySlug) : null;
  // Extract country display name from the page title (before the holidays word)
  const localCountryName = countryEntry
    ? countryEntry.pageTitle.split(countryEntry.holidaysWord)[0].trim()
    : countryName;

  // Use cached translation title if available, otherwise fall back to English/titleLocal
  const translatedTitle = cachedTranslation?.title;
  const translatedLocal = cachedTranslation?.titleLocal ?? event.titleLocal;
  const hasLocal = translatedLocal && translatedLocal !== event.title;

  // For SEO title: prefer "translated (english)" for bilingual value, or just translated
  const titleDisplay = translatedTitle
    ? (translatedTitle !== event.title ? `${translatedTitle} (${event.title})` : event.title)
    : (hasLocal ? `${translatedLocal} (${event.title})` : event.title);

  // Format date with weekday in target language
  const formattedDate = formatDateLong(event.dateStart, lang);
  const uniSuffix = uniLabel ? ` — ${uniLabel}` : '';
  const rawTitle = `${titleDisplay}${uniSuffix} — ${formattedDate} | ${SITE_NAME}`;
  const title = guardTitleLength(rawTitle, SITE_NAME);

  const primaryName = translatedTitle || (hasLocal ? translatedLocal! : event.title);
  const translatedNotes = cachedTranslation?.notes;
  const notesText = translatedNotes || event.notes || '';
  const isPublicHoliday = event.type === 'public_holiday' || event.isGazetted;
  const isGlobal = event.country === 'XR';

  let description: string;
  if (isPublicHoliday && !isGlobal) {
    description = `${primaryName} — ${formattedDate}, ${localCountryName}.`;
    if (notesText) description += ` ${notesText}`;
  } else if (isGlobal) {
    description = `${primaryName} — ${formattedDate}.`;
    if (notesText) description += ` ${notesText}`;
  } else if (uniLabel) {
    description = `${primaryName} — ${uniLabel}, ${formattedDate}.`;
    if (notesText) description += ` ${notesText}`;
  } else {
    description = `${primaryName} — ${formattedDate} (${localCountryName}).`;
    if (notesText) description += ` ${notesText}`;
  }

  const keywords = [
    `${primaryName} ${YEAR}`,
    ...(isGlobal ? [] : [`${localCountryName} ${primaryName}`]),
    `${event.type.replace(/_/g, ' ')} ${isGlobal ? YEAR : localCountryName}`,
    ...(isGlobal ? [] : [`${localCountryName} holidays ${YEAR}`]),
  ];

  return { title, description: truncateDescription(description), keywords, canonical };
}

/** Builds SEO metadata for a university academic calendar page */
export function getUniversitySeoData(universityId: string, lang: string = 'en', academicYear?: number) {
  const uni = getUniversityById(universityId);
  if (!uni) return null;

  const entityKey = academicYear ? `${universityId}:${academicYear}` : universityId;
  const events = getEventsForUniversity(entityKey);
  const cityName = formatUniversityCityName(uni.city);
  const slug = getUniversitySlugForId(universityId, lang, academicYear);
  const canonical = slug ? `${SITE_URL}/${slug}` : `${SITE_URL}/universities/${universityId}`;

  const yearRange = academicYear ? `${academicYear}/${academicYear + 1}` : (() => {
    const years = events.map(e => parseInt(e.dateStart.slice(0, 4), 10));
    const minYear = years.length > 0 ? Math.min(...years) : YEAR;
    const maxYear = years.length > 0 ? Math.max(...years) : YEAR + 1;
    return minYear === maxYear ? `${minYear}/${minYear + 1}` : `${minYear}/${maxYear}`;
  })();

  const country = getCountryByCode(uni.country);
  const countryName = country?.name ?? uni.country;

  // Use i18n slug entry for translated title/description when available
  const slugEntry = getUniversitySlugEntry(slug);
  // Tighter fallback (almanak-uj7qp.12): SERP-truncates around 60 chars, so
  // drop the full name + brand suffix. Google appends the site name itself.
  const title = slugEntry?.pageTitle ?? `${uni.abbrev} Academic Calendar ${yearRange}`;
  const description = slugEntry?.pageDescription ?? `${uni.name} (${uni.abbrev}) academic calendar ${yearRange}: ${events.length} key dates including semester start, exams, enrollment & graduation in ${cityName}, ${countryName}.`;

  const COUNTRY_KEYWORDS: Partial<Record<CountryCode, (abbrev: string, yr: string) => string[]>> = {
    ID: (abbrev, yr) => [`${abbrev} kalender akademik ${yr}`, `${abbrev} jadwal kuliah ${yr}`],
    MY: (abbrev, yr) => [`${abbrev} kalendar akademik ${yr}`, `${abbrev} jadual kuliah ${yr}`],
    PH: (abbrev, yr) => [`${abbrev} academic schedule ${yr}`, `${abbrev} school calendar ${yr}`],
  };
  const localKeywords = COUNTRY_KEYWORDS[uni.country]?.(uni.abbrev, yearRange) ?? [];

  const keywords = [
    `${uni.abbrev} academic calendar ${yearRange}`,
    `${uni.name} calendar ${yearRange}`,
    ...localKeywords,
    `${cityName} university calendar`,
  ];

  return { title, description, keywords, canonical, university: uni, events };
}

/** SEO data for month hub pages */
export function getMonthSeoData(month: number, year: number) {
  const monthName = getMonthDisplay(month);
  const title = `Holidays in ${monthName} ${year} — ${SITE_NAME}`;
  const description = `Browse ${monthName} ${year} holidays across 14 countries. Compare, filter by type, and export to Google Calendar.`;
  const canonical = `${SITE_URL}/${monthName.toLowerCase()}-${year}-worldwide-calendar`;
  const keywords = [
    `holidays ${monthName} ${year}`,
    `public holidays ${monthName} ${year}`,
    `${monthName} ${year} calendar`,
    `festivals ${monthName} ${year}`,
  ];
  return { title, description, keywords, canonical };
}

/** SEO data for event type hub pages */
export function getTypeSeoData(eventType: EventType) {
  const displayName = TYPE_DISPLAY[eventType] || eventType.replace(/_/g, ' ');
  const title = `${displayName} ${YEAR} — Every Country | ${SITE_NAME}`;
  const description = `${displayName} ${YEAR} across 14 countries. Filter by month, region, or religion. Free interactive calendar with export.`;
  const slug = eventType.replace(/_/g, '-') + 's';
  // Handle plural edge cases
  const slugMap: Record<string, string> = {
    public_holiday: 'public-holidays',
    national_day: 'national-days',
    observance: 'observances',
    cultural_festival: 'cultural-festivals',
    local_event: 'local-events',
  };
  const canonical = `${SITE_URL}/${slugMap[eventType] || slug}-worldwide-calendar`;
  const keywords = [
    `${displayName.toLowerCase()} ${YEAR}`,
    `${displayName.toLowerCase()} worldwide`,
    `${displayName.toLowerCase()} all countries`,
  ];
  return { title, description, keywords, canonical };
}

/** SEO data for religion hub pages */
export function getReligionHubSeoData(subLens: ReligionSubLensId) {
  const displayName = RELIGION_DISPLAY_NAMES[subLens] || subLens;
  const title = `${displayName} Holidays ${YEAR} — Calendar & Dates | ${SITE_NAME}`;
  const description = `${displayName} holidays and observances in ${YEAR}: dates across multiple countries with cultural context and calendar.`;
  const canonical = `${SITE_URL}/${subLens}-calendar-${YEAR}-worldwide-calendar`;
  const keywords = [
    `${displayName.toLowerCase()} holidays ${YEAR}`,
    `${displayName.toLowerCase()} calendar ${YEAR}`,
    `${displayName.toLowerCase()} festivals ${YEAR}`,
    `${displayName.toLowerCase()} observances`,
  ];
  return { title, description, keywords, canonical };
}

/** SEO data for nature hub page */
export function getNatureHubSeoData() {
  const title = `Southeast Asia Weather Calendar ${YEAR} — Monsoon, Dry Season & Travel Guide | ${SITE_NAME}`;
  const description = `Monsoon season dates, dry season windows, and weather calendar across 17 countries in ${YEAR}. Plan travel around seasonal patterns.`;
  const canonical = `${SITE_URL}/nature-calendar-${YEAR}-worldwide-calendar`;
  const keywords = [
    `monsoon season southeast asia calendar ${YEAR}`,
    `dry season dates by country asia ${YEAR}`,
    `weather calendar asia travel planning`,
    `typhoon season philippines ${YEAR}`,
    `rainy season thailand ${YEAR}`,
    `best month southeast asia weather comparison`,
    `cherry blossom shillong ${YEAR}`,
    `dust storm season iraq travel`,
    `nature calendar ${YEAR}`,
  ];
  return { title, description, keywords, canonical };
}

/** SEO data for long weekend pages */
export function getLongWeekendSeoData(code: CountryCode) {
  const country = getCountryByCode(code);
  if (!country) return null;
  const title = `Long Weekends in ${country.name} ${YEAR} — ${SITE_NAME}`;
  const description = `Calculate optimal bridge days around ${country.name}'s ${YEAR} holidays. Plan leave, maximize days off, and export to your calendar.`;
  const slug = `${country.name.toLowerCase().replace(/\s+/g, '-')}-long-weekends-${YEAR}`;
  const canonical = `${SITE_URL}/${slug}`;
  const keywords = [
    `long weekends ${country.name} ${YEAR}`,
    `bridge days ${country.name}`,
    `${country.name} holidays ${YEAR}`,
    `days off ${country.name} ${YEAR}`,
  ];
  return { title, description, keywords, canonical, country };
}

/** Builds SEO metadata for a country religion page */
export function getCountryReligionSeoData(code: CountryCode, lang: string = 'en') {
  const country = getCountryByCode(code);
  if (!country) return null;

  const events = getEventsForReligionByCountry(code);
  const slug = getCountryLensSlugForId(code, 'religion', lang);
  const canonical = slug ? `${SITE_URL}/${slug}` : `${SITE_URL}/${code.toLowerCase()}-religious-holidays-${YEAR}`;

  const subLensCounts: Record<string, number> = {};
  for (const e of events) {
    const sub = e.subLens || 'other';
    subLensCounts[sub] = (subLensCounts[sub] || 0) + 1;
  }
  const breakdown = Object.entries(subLensCounts)
    .map(([sub, count]) => `${RELIGION_DISPLAY_NAMES[sub] || sub}: ${count}`)
    .join(', ');

  const lensEntry = getCountryLensSlugEntry(slug);
  const title = lensEntry?.pageTitle ?? `${country.name} Religious Holidays ${YEAR} — ${SITE_NAME}`;
  const description = lensEntry?.pageDescription ?? `${country.flag} ${country.name} ${YEAR}: ${events.length} religious holidays (${breakdown}). Free interactive calendar.`;

  const keywords = [
    `${country.name} religious holidays ${YEAR}`,
    `${country.name} Islamic holidays ${YEAR}`,
    `${country.name} Hindu holidays ${YEAR}`,
    `${country.name} religious calendar ${YEAR}`,
  ];

  return { title, description, keywords, canonical, country, events };
}

/** Builds SEO metadata for a country nature page */
export function getCountryNatureSeoData(code: CountryCode, lang: string = 'en') {
  const country = getCountryByCode(code);
  if (!country) return null;

  const events = getEventsForNatureByCountry(code);
  const slug = getCountryLensSlugForId(code, 'nature', lang);
  const canonical = slug ? `${SITE_URL}/${slug}` : `${SITE_URL}/${code.toLowerCase()}-weather-seasons-${YEAR}`;

  const lensEntry = getCountryLensSlugEntry(slug);
  const title = lensEntry?.pageTitle ?? `${country.name} Weather by Month ${YEAR} — Seasons & Travel Guide | ${SITE_NAME}`;
  const description = lensEntry?.pageDescription ?? `${country.flag} ${country.name} ${YEAR}: ${events.length} seasonal events — monsoons, dry seasons & extreme weather. Free interactive calendar.`;

  const keywords = [
    `${country.name} weather ${YEAR}`,
    `${country.name} monsoon season ${YEAR}`,
    `${country.name} dry season ${YEAR}`,
    `${country.name} seasons calendar`,
  ];

  return { title, description, keywords, canonical, country, events };
}
